import React, { FC, useEffect, useState, useMemo } from 'react';

import { ArrowBack, Beenhere, Share } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  Container,
  Typography,
  LinearProgress,
  linearProgressClasses,
  Divider,
  styled,
  SxProps,
  Avatar,
  Theme,
  useMediaQuery,
  Chip,
  useTheme,
} from '@mui/material';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router';
import shallow from 'zustand/shallow';

import { Card } from '../../../components/atoms/Card';
import { CountdownTimer } from '../../../components/community/sidebar/CountdownTimer';
import { VotingModal } from '../../../components/community/voting/VotingModal';
import { PgxIcon } from '../../../components/layout/icons/pgx-icon';
import { InputRadio } from '../../../components/molecules/InputRadio';
import { StatusChip } from '../../../components/molecules/StatusChip';
import { TooltipCopy } from '../../../components/molecules/TooltipCopy';
import { UserTag } from '../../../components/molecules/UserTag';
import { ProposalComments } from '../../../components/organisms/ProposalComments';
import { ProposalVotes } from '../../../components/organisms/ProposalVotes';
import { ShareModal } from '../../../components/organisms/ShareModal';
import { CentralisedLoading } from '../../../components/utils/CentralisedLoading';
import { QuillEditor } from '../../../components/utils/quill-editor';
import { useAnalytics } from '../../../hooks/dataTracking/useAnalytics';
import { useErrorToast } from '../../../hooks/useErrorToast';
import { EProposalStatus } from '../../../interfaces/proposal';
import { useAuthStore } from '../../../stores/auth';
import { useSingleProposalStore } from '../../../stores/singleProposal';
import { abbreviateNumber, abbreviateId, formatDateTime, isAddressFromPegaxyTeam } from '../../../utils/humanizer';

interface ResultBarsProps {
  option: string;
  percentage: number;
  sx?: SxProps;
}

const LinearProgressStyled = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
  },
}));

const ResultBars: FC<ResultBarsProps> = ({ option, percentage, sx }) => {
  return (
    <Box sx={{ ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
        <Typography variant="h6" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', mr: 2 }}>
          {option}
        </Typography>
        <Typography variant="h6">{percentage.toFixed(2)}%</Typography>
      </Box>
      <LinearProgressStyled variant="determinate" value={percentage} />
    </Box>
  );
};

interface CountdownCardProps {
  startDate?: Date;
  endDate?: Date;
  proposalStatus?: EProposalStatus;
}

const CountdownCard: FC<CountdownCardProps> = ({ startDate, endDate, proposalStatus }) => {
  const [timerFinished, setTimerFinished] = useState(false);

  switch (proposalStatus) {
    case EProposalStatus.Active:
      if (moment().isAfter(endDate) || timerFinished)
        return <Card titleBoxColor="orange" title="Voting Ended" sx={{ mt: 3, mb: 3 }} />;
      return (
        <Card titleBoxColor="orange" title="Voting ends in" sx={{ mt: 3, mb: 3 }}>
          <CountdownTimer endDate={moment(endDate).valueOf()} timerFinished={() => setTimerFinished(true)} />
        </Card>
      );

    case EProposalStatus.Pending:
      if (moment().isAfter(startDate) || timerFinished)
        return <Card titleBoxColor="orange" title="Voting starts shortly" sx={{ mt: 3, mb: 3 }} />;
      return (
        <Card titleBoxColor="orange" title="Voting starts in" sx={{ mt: 3, mb: 3 }}>
          <CountdownTimer endDate={moment(startDate).valueOf()} timerFinished={() => setTimerFinished(true)} />
        </Card>
      );

    default:
      return <Card titleBoxColor="orange" title="Voting Ended" sx={{ mt: 3, mb: 3 }} />;
  }
};

export const ProposalPage: FC = () => {
  const { sendButtonClickTrackingEvent } = useAnalytics();
  const params = useParams();
  const { id } = params;
  const navigate = useNavigate();
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'), {
    noSsr: true,
  });
  const isDarkTheme = useTheme().palette.mode === 'dark';

  const storedToken = useAuthStore((store) => store.storedToken);
  console.log({ storedToken });
  const { proposal, loading, error, getSingleProposal, clearSingleProposal, voteSingleProposal } =
    useSingleProposalStore(
      ({ proposal, loading, error, getSingleProposal, clearSingleProposal, voteSingleProposal }) => ({
        proposal,
        loading,
        error,
        getSingleProposal,
        clearSingleProposal,
        voteSingleProposal,
      }),
      shallow,
    );

  useErrorToast(error);

  const {
    _id,
    title,
    status,
    voters,
    summary,
    problem,
    appetite,
    solution,
    rabbitHoles,
    noGos,
    voteOptions,
    results,
    comments,
    creatorAddress,
    startDate,
    endDate,
    feedback,
  } = proposal || {};
  // Following variables are memoized with voters?.length
  // This is as data on the page will change only if there is a vote
  // Making comments will not affect vote values

  // Accumulates all of the voters' networth
  const upvotersNetWorth = useMemo(() => voters?.reduce((acc, obj) => acc + obj.netWorth, 0) || 0, [voters?.length]);
  // Processes vote options into a format acceptable by the radio buttons
  const nextVoteOptions = useMemo(
    () => voteOptions?.map((option) => ({ title: option, value: option })) || [],
    [voters?.length],
  );

  const [voteOption, setVoteOption] = useState('');
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [votingModalOpen, setVotingModalOpen] = useState<boolean>(false);

  useEffect(() => {
    // Get Current Proposal once the User is on the Page
    if (id) getSingleProposal(id);
  }, [id]);

  useEffect(() => {
    // Clear Proposal and Error on Dismount
    // So that previous proposal will not show when navigating back to page
    return () => {
      clearSingleProposal();
    };
  }, []);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVoteOption((event.target as HTMLInputElement).value);
  };

  const handleVote = () => {
    voteSingleProposal({ accessToken: storedToken, proposalId: _id, voteOption: voteOption });
    setVotingModalOpen(false);
    sendButtonClickTrackingEvent('Click Vote on proposal');
  };

  const isProposalActive = status === EProposalStatus.Active;

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {proposal && (
        <ShareModal
          open={shareModalOpen}
          handleClose={() => {
            setShareModalOpen(false);
            sendButtonClickTrackingEvent('Close share window');
          }}
          proposal={proposal}
        />
      )}

      <VotingModal
        open={votingModalOpen}
        handleVote={handleVote}
        handleClose={() => {
          setVotingModalOpen(false);
          sendButtonClickTrackingEvent('Close Vote window');
        }}
        voteOption={voteOption}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 7,
        }}
      >
        <Container maxWidth={'lg'} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ width: { xs: '100%', md: 'calc(100% - 321px)' }, pr: { xs: 0, md: 4 } }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => {
                navigate(-1);
                sendButtonClickTrackingEvent('Navigate back to proposal list page from single proposal page');
              }}
              sx={{ mb: 3 }}
            >
              Back
            </Button>
            {loading && <CentralisedLoading sx={{ mt: 6 }} />}
            {proposal && (
              <>
                {feedback && (
                  <Box sx={{ backgroundColor: isDarkTheme ? "neutral.800" : "neutral.200", borderRadius: 1, p: 1.5, mb: 2, display: 'flex' }}>
                    <Beenhere color="primary" sx={{ mr: 1, mt: 0.25, fontSize: 32 }} />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography color="primary" variant="caption" fontWeight="bold" >
                        PEGAXY TEAM FEEDBACK
                      </Typography>
                      <Typography variant="body1" sx={{ mt: 0.5 }}>
                        {feedback}
                      </Typography>
                    </Box>

                  </Box>
                )}
                <Typography variant="h3" sx={{ mb: 0.5, overflowWrap: 'anywhere' }}>
                  {title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <StatusChip status={status} sx={{ mr: { xs: 0.3, sm: 1 } }} />
                    {isAddressFromPegaxyTeam(creatorAddress) && (
                      <Chip
                        sx={{ mr: { xs: 0.3, sm: 1 } }}
                        avatar={<Avatar src="https://pegaxy.io/wp-content/uploads/2021/09/cropped-logo-seo.jpg" />}
                        label="Pegaxy Team"
                        size="small"
                      />
                    )}

                    <Chip
                      avatar={
                        <Avatar sx={{ backgroundColor: 'neutral.800' }}>
                          <PgxIcon sx={{ height: 16, width: 16 }} />
                        </Avatar>
                      }
                      label={abbreviateNumber(upvotersNetWorth)}
                      size="small"
                    />
                  </Box>
                  <Button
                    startIcon={<Share />}
                    onClick={() => {
                      setShareModalOpen(true);
                      sendButtonClickTrackingEvent('Open share window');
                    }}
                  >
                    Share
                  </Button>
                </Box>

                {/* Beginning of Main Section of Proposal */}
                <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                  Summary
                </Typography>
                {summary?.split(/\r\n|\r|\n/g).map((line: any) => {
                  if (line)
                    return (
                      <Typography variant="body1" color="text.secondary">
                        {line}
                      </Typography>
                    );
                  return <br />;
                })}

                <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                  Problem
                </Typography>
                <QuillEditor readOnly value={problem} />

                <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                  Appetite
                </Typography>
                <QuillEditor readOnly value={appetite} />

                <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                  Solution
                </Typography>
                <QuillEditor readOnly value={solution} />

                <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                  Rabbit Holes
                </Typography>
                <QuillEditor readOnly value={rabbitHoles} />

                <Typography variant="h4" sx={{ mb: 2, mt: 3 }}>
                  No-gos
                </Typography>
                <QuillEditor readOnly value={noGos} />

                <Divider sx={{ width: '50%', my: 4, mx: 'auto', textAlign: 'center' }} />

                <Card titleBoxColor="blue" title="Cast your vote" sx={{ mt: 5 }}>
                  <InputRadio
                    value={voteOption}
                    handleChange={handleRadioChange}
                    options={nextVoteOptions}
                    disabled={!isProposalActive || loading}
                  />
                  <LoadingButton
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{ mt: 3 }}
                    onClick={() => {
                      setVotingModalOpen(true);
                      sendButtonClickTrackingEvent('Open Vote window');
                    }}
                    disabled={!voteOption || !isProposalActive}
                    loading={loading}
                  >
                    Vote
                  </LoadingButton>
                </Card>
                {mdUp && <ProposalComments comments={comments || []} />}
              </>
            )}
          </Box>

          {/* Start of SideBar on the Right */}
          {proposal && (
            <Box sx={{ width: { xs: '100%', md: '321px' }, mt: { xs: 3, md: 0 } }}>
              <Card titleBoxColor="green" title="Information">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Proposal ID
                  </Typography>
                  <TooltipCopy value={params.id} placement="top-end">
                    <Typography variant="body2" fontWeight={600}>
                      #{abbreviateId(params.id)}
                    </Typography>
                  </TooltipCopy>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Submitter
                  </Typography>
                  <Box sx={{ display: 'flex' }}>
                    <TooltipCopy
                      value={creatorAddress}
                      placement="top-end"
                      hiddeTooltip={isAddressFromPegaxyTeam(creatorAddress)}
                    >
                      <UserTag
                        publicAddress={creatorAddress}
                        typographyProps={{ fontWeight: 'bold', color: 'text.primary' }}
                      />
                    </TooltipCopy>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Start date
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDateTime(startDate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    End date
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formatDateTime(endDate)}
                  </Typography>
                </Box>
              </Card>

              {results?.length && (
                <Card titleBoxColor="purple" title="Current results" sx={{ mt: 3 }}>
                  {results.map((result, index) => (
                    <ResultBars
                      option={result.key}
                      percentage={result.percentage * 100.0}
                      sx={{ mt: index !== 0 ? 2.5 : 0 }}
                    />
                  ))}
                </Card>
              )}
              <CountdownCard endDate={endDate} startDate={startDate} proposalStatus={status} />
              <ProposalVotes votes={voters || []} />
              {!mdUp && <ProposalComments comments={comments || []} />}
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};
